import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useTheme } from '@/context/ThemeContext';
import { useSidebar } from '@/context/SidebarContext';
import { Menu, X, Shield, Sun, Moon } from 'lucide-react';
import { Button } from '@/components/ui/button';

export function Navbar() {
    const { dbUser } = useAuth();
    const { config, theme, toggleTheme } = useTheme();
    const { isOpen, toggle } = useSidebar();
    const navigate = useNavigate();
    const location = useLocation();

    // Check if we are in a dashboard route
    const isRecruiterDashboard = location.pathname.startsWith('/dashboard');
    const isCandidateDashboard = location.pathname.startsWith('/candidate/dashboard');
    const isOnBoarding = location.pathname.startsWith('/onboarding');
    const hasRecruiterRole = dbUser?.roles?.includes('recruiter') || dbUser?.role === 'recruiter';
    const hasCandidateRole = dbUser?.roles?.includes('candidate') || dbUser?.role === 'candidate';

    return (
        <nav className="fixed top-0 w-full z-50 bg-background/80 backdrop-blur-md border-b-0.2 border-black/5 dark:border-border transition-colors duration-300">
            <div className="w-full px-8 h-16 flex items-center justify-between">
                <div className="flex items-center gap-4">
                    {/* Mobile Menu Toggle - Only show on dashboard and mobile */}
                    {dbUser && isRecruiterDashboard && (
                        <button
                            onClick={toggle}
                            className="md:hidden text-muted-foreground hover:text-foreground"
                        >
                            {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
                        </button>
                    )}

                    {/* add default color of white to the below div */}
                    <div
                        className="flex items-center gap-2 font-bold text-xl tracking-tight cursor-pointer text-foreground"
                        onClick={() => navigate('/')}
                    >
                        {config.logo ? (
                            <img src={config.logo} alt={config.companyName} className="h-8 w-auto object-contain" />
                        ) : (
                            <div className="w-8 h-8 bg-primary/20 rounded-lg flex items-center justify-center border border-primary/20">
                                <Shield className="w-5 h-5 text-primary" />
                            </div>
                        )}
                        <span>{config.companyName}</span>
                    </div>
                </div>

                <div className="flex items-center gap-6 text-sm font-medium text-muted-foreground">
                    {/* Public Pages Navigation - Hide on Recruiter Dashboard */}
                    {!isRecruiterDashboard && !isCandidateDashboard && (
                        <>
                            {location.pathname === '/candidate' ? (
                                <button onClick={() => navigate('/')} className="hidden md:block hover:text-foreground transition-colors">For Recruiters</button>
                            ) : (
                                <button onClick={() => navigate('/candidate')} className="hidden md:block hover:text-foreground transition-colors">For Candidates</button>
                            )}
                        </>
                    )}

                    {!isRecruiterDashboard && !isCandidateDashboard && (
                        <>
                            <button onClick={() => navigate('/privacy')} className="hidden md:block hover:text-foreground transition-colors">Privacy</button>
                            <button onClick={() => navigate('/contact')} className="hidden md:block hover:text-foreground transition-colors">Contact Us</button>
                        </>
                    )}

                    {/* Switch View for Dual Role Users - Recruiter View */}
                    {isRecruiterDashboard && hasCandidateRole && (
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => navigate('/candidate/dashboard')}
                            className="hidden md:flex"
                        >
                            Switch to Candidate
                        </Button>
                    )}

                    {/* Theme Toggle */}
                    <button
                        onClick={toggleTheme}
                        className="p-2 rounded-full hover:bg-muted transition-colors text-muted-foreground hover:text-foreground"
                        title={`Switch to ${theme === 'dark' ? 'light' : 'dark'} mode`}
                    >
                        {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
                    </button>

                    {!dbUser && !isOnBoarding &&
                        <Button size="sm" onClick={() => navigate('/login')} className="!bg-white !text-gray-900 hover:!bg-gray-100">
                            Login
                        </Button>
                    }

                    {/* Dashboard CTA or Switch to Recruiter */}
                    {dbUser && !isOnBoarding && !isRecruiterDashboard && (
                        isCandidateDashboard && hasRecruiterRole ? (
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => navigate('/dashboard')}
                                className="hidden md:flex"
                            >
                                Switch to Recruiter
                            </Button>
                        ) : (
                            !isCandidateDashboard && (
                                <Button size="sm" onClick={() => navigate('/dashboard')} className="!bg-white !text-gray-900 hover:!bg-gray-100">
                                    Dashboard
                                </Button>
                            )
                        )
                    )}
                </div>
            </div>
        </nav>
    );
}
